import { useCallback } from 'react';
import useCustomAuth from './useCustomAuth';
import { ROLE_CAPABILITIES } from '../../Constants/permissions';

/**
 * Custom hook to evaluate user permissions & capabilities cleanly across UI components.
 * Eliminates hardcoded role checks in pages.
 */
export default function usePermissions() {
  const { user, isInitialized, isAuthenticated } = useCustomAuth();

  const can = useCallback((permission) => {
    if (!user) return false;

    // 1. Direct permission claim evaluation (if present in JWT / Auth state)
    if (Array.isArray(user.permissions) && user.permissions.includes(permission)) {
      return true;
    }

    // 2. Role-based capability fallback from matrix
    const userRole = user.role ?? 'USER';
    const capabilities = ROLE_CAPABILITIES[userRole] ?? [];

    return capabilities.includes(permission);
  }, [user]);

  const hasPlanFeature = useCallback((featureKey) => {
    if (!user) return false;
    if (user.role === 'GATEWAY_ADMIN') return true;
    return Array.isArray(user.planFeatures) ? user.planFeatures.includes(featureKey) : false;
  }, [user]);

  return {
    can,
    hasPlanFeature,
    user,
    isInitialized: isInitialized ?? true,
    isAuthenticated: isAuthenticated ?? false,
    role: user?.role ?? 'USER',
    isGatewayAdmin: user?.role === 'GATEWAY_ADMIN',
    isClientAdmin: user?.role === 'CLIENT_ADMIN',
  };
}
